"use client";

import React, { useRef, useLayoutEffect } from "react";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styles from "./BrandingStatement.module.scss";

gsap.registerPlugin(SplitText, ScrollTrigger);

const BrandingStatement = () => {
	const sectionRef = useRef<HTMLDivElement>(null);
	const statementRef = useRef<HTMLHeadingElement>(null);
	const imagesRef = useRef<(HTMLSpanElement | null)[]>([]);

	useLayoutEffect(() => {
		if (!sectionRef.current || !statementRef.current) return;

		const ctx = gsap.context(() => {
			// Set initial state for the entire statement
			gsap.set(statementRef.current, { opacity: 1 });

			// Hide images initially using querySelector (more reliable than refs)
			const imageWrappers = statementRef.current!.querySelectorAll(`.${styles.brandingStatement__imageWrapper}`);
			gsap.set(imageWrappers, { scale: 0, opacity: 0 });

			// Split the text into lines
			const split = new SplitText(statementRef.current, {
				type: "lines",
				linesClass: "line++",
			});
			const lines = split.lines as HTMLElement[];

			// Add overflow hidden to parent line containers
			lines.forEach((line) => {
				const parent = line.parentElement;
				if (parent) {
					parent.style.overflow = "hidden";
					parent.style.display = "block";
				}
			});

			// Re-query images after split - DOM structure may have changed
			const imagesAfterSplit = statementRef.current!.querySelectorAll(`.${styles.brandingStatement__imageWrapper}`);

			// Force images to stay inline-block and ensure they're visible in DOM
			imagesAfterSplit.forEach((img) => {
				const element = img as HTMLElement;
				element.style.display = "inline-block";
				element.style.visibility = "visible";
			});

			// Set initial state for text lines
			gsap.set(lines, { y: "115%" });

			// Create timeline
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: sectionRef.current,
					start: "top 80%",
					once: true,
					onEnter: () => console.log("BrandingStatement animation triggered"),
				},
			});

			// Animate text lines
			tl.to(lines, {
				y: "0%",
				duration: 1,
				stagger: 0.1,
				ease: "expo.out",
			});

			// Set overflow visible after animation
			lines.forEach((line) => {
				const parent = line.parentElement;
				if (parent) {
					tl.to(
						parent,
						{
							overflow: "visible",
							duration: 0.1,
						},
						"<+=0.3"
					);
				}
			});

			// Animate images after text - make visible and scale up
			tl.to(
				imagesAfterSplit,
				{
					opacity: 1,
					scale: 1,
					duration: 0.8,
					stagger: 0.15,
					ease: "back.out(1.7)",
				},
				"-=0.8"
			);

			// Clear GSAP transforms and set random hover rotation for each image
			tl.call(() => {
				imagesAfterSplit.forEach((img) => {
					const element = img as HTMLElement;
					// Generate random rotation for this image
					const randomRotation = gsap.utils.random(-12, 12);
					// Store it as a CSS variable
					element.style.setProperty("--hover-rotation", `${randomRotation}deg`);
					// Clear GSAP inline transform
					gsap.set(element, { clearProps: "transform" });
					// Enable hover interactions
					element.classList.add(styles.brandingStatement__imageWrapper_animated);
				});
			});

			return () => {
				split.revert();
			};
		}, sectionRef);

		return () => ctx.revert();
	}, []);

	return (
		<section ref={sectionRef} className={styles.brandingStatement}>
			<div className={styles.brandingStatement__content}>
				<h2 ref={statementRef} className={styles.brandingStatement__statement}>
					We are a branding{" "}
					<span
						className={styles.brandingStatement__imageWrapper}
						ref={(el) => {
							imagesRef.current[0] = el;
						}}
					>
						<img src="/images/brand.svg" alt="Brand icon" className={styles.brandingStatement__icon} />
					</span>{" "}
					and digital design{" "}
					<span
						className={styles.brandingStatement__imageWrapper}
						ref={(el) => {
							imagesRef.current[1] = el;
						}}
					>
						<img src="/images/web.svg" alt="Globe icon" className={styles.brandingStatement__icon} />
					</span>{" "}
					studio dedicated to bridging the gap{" "}
					<span
						className={styles.brandingStatement__imageWrapper}
						ref={(el) => {
							imagesRef.current[2] = el;
						}}
					>
						<img src="/images/slant-cross.svg" alt="Plus icon" className={styles.brandingStatement__icon} />
					</span>{" "}
					between brands and people, celebrating culture{" "}
					<span
						className={styles.brandingStatement__imageWrapper}
						ref={(el) => {
							imagesRef.current[3] = el;
						}}
					>
						<img src="/images/flame.svg" alt="Fire icon" className={styles.brandingStatement__icon} />
					</span>{" "}
					and craftsmanship.
				</h2>
				<p className={styles.brandingStatement__clients}>We’ve collaborated with notable partners like Access Bank, o3 Cards, Ronke Adeyemi & Co. Law Firm, Vairam Edit, LVI Media, and Casa, among others.</p>
			</div>
		</section>
	);
};

export default BrandingStatement;
